// src/components/admin/batches/TutorMatchmakingModal.tsx
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "src/components/ui/Dialog";
import { Button } from "src/components/ui/Button";
import { Badge } from "src/components/ui/badge";
import { Loader2, Sparkles, Check, Clock, Users, BookOpen, AlertCircle } from "lucide-react";
import { TutorMatchCriteria, RankedTutorMatch } from "src/types/tutorMatchmaking";
import { fetchAndCalculateTutorMatches } from "src/services/tutorMatchmakingService";

interface TutorMatchmakingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTutor: (
    tutorId: string,
    tutorName: string,
    matchScore: number,
    matchLabel: string
  ) => void;
  criteria: TutorMatchCriteria;
}

export const TutorMatchmakingModal: React.FC<TutorMatchmakingModalProps> = ({
  isOpen,
  onClose,
  onSelectTutor,
  criteria,
}) => {
  const [candidates, setCandidates] = useState<RankedTutorMatch[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    setIsLoading(true);
    setError(null);

    fetchAndCalculateTutorMatches(criteria)
      .then((results) => {
        if (isMounted) {
          setCandidates(results);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          console.error("Matchmaking error:", err);
          setError("Failed to calculate matches. Please try again.");
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, criteria.subjectName, criteria.studentTimezone, criteria.board, criteria.educationLevel]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 overflow-hidden sm:rounded-2xl">
        {/* Header */}
        <DialogHeader className="px-6 py-4 bg-[#faf9fe] border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <DialogTitle className="text-sm font-bold text-slate-900">
                Tutor Matchmaking Assistant
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 mt-0.5">
                Calculates compatibility on demand based on Subject, Timezone & Capacity
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Target Context Strip */}
        <div className="px-6 py-2.5 bg-slate-50 border-b border-slate-200 text-xs flex flex-wrap items-center justify-between gap-2 text-slate-600">
          <div>
            Target Criteria:{" "}
            <span className="font-semibold text-purple-700">
              {criteria.subjectName || "All Subjects"}
            </span>
            {criteria.board && (
              <>
                {" "}• <span className="font-medium text-slate-800">{criteria.board}</span>
              </>
            )}
            {criteria.educationLevel && (
              <>
                {" "}• <span className="font-medium text-slate-800">{criteria.educationLevel}</span>
              </>
            )}
            {criteria.studentTimezone && (
              <>
                {" "}• <span className="font-mono text-slate-700">{criteria.studentTimezone}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-emerald-700 font-semibold text-[11px]">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            Rules Computed on Demand
          </div>
        </div>

        {/* Modal Body / Candidates List */}
        <div className="p-6 overflow-y-auto space-y-3 flex-1">
          {isLoading && (
            <div className="py-12 flex flex-col items-center justify-center text-center space-y-3">
              <Loader2 className="w-7 h-7 text-purple-600 animate-spin" />
              <p className="text-xs text-slate-500 font-medium">
                Evaluating active tutors across subject match, timezone overlap, and batch load...
              </p>
            </div>
          )}

          {error && !isLoading && (
            <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-3 text-rose-800 text-xs">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold">Match calculation error</p>
                <p className="mt-0.5">{error}</p>
              </div>
            </div>
          )}

          {!isLoading && !error && candidates.length === 0 && (
            <div className="py-12 text-center text-xs text-slate-500">
              No matching tutors found for this criteria. You can manually assign any tutor from the dropdown list.
            </div>
          )}

          {!isLoading &&
            !error &&
            candidates.map((candidate, idx) => {
              const initials = `${candidate.firstName?.[0] || ""}${candidate.lastName?.[0] || ""}`.toUpperCase() || "TU";
              const isTopMatch = idx === 0 && candidate.overallScore >= 80;

              return (
                <div
                  key={candidate.tutorId}
                  className={`p-4 rounded-xl border transition space-y-3 ${
                    isTopMatch
                      ? "border-purple-300 bg-purple-50/20 shadow-2xs hover:border-purple-500"
                      : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-2xs"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center shrink-0 text-xs">
                        {initials}
                      </div>
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-bold text-slate-900">
                            {candidate.firstName} {candidate.lastName}
                          </h4>
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-mono font-bold border ${
                              candidate.overallScore >= 80
                                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                : candidate.overallScore >= 60
                                ? "bg-purple-50 text-purple-800 border-purple-200"
                                : "bg-slate-100 text-slate-700 border-slate-200"
                            }`}
                          >
                            {candidate.overallScore}% Calculated Match
                          </span>
                        </div>
                        <p className="text-xs text-slate-500">
                          {candidate.recommendationReason}
                        </p>
                      </div>
                    </div>

                    <Button
                      size="sm"
                      onClick={() =>
                        onSelectTutor(
                          candidate.tutorId,
                          `${candidate.firstName} ${candidate.lastName}`,
                          candidate.overallScore,
                          candidate.matchLabel
                        )
                      }
                      className={
                        isTopMatch
                          ? "bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold h-8 px-3.5 shadow-xs"
                          : "bg-white hover:bg-purple-50 text-purple-700 border border-purple-300 text-xs font-semibold h-8 px-3.5 shadow-2xs"
                      }
                    >
                      Select Tutor
                    </Button>
                  </div>

                  {/* 3-Criteria Breakdown Pills */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2 border-t border-slate-100 text-[11px]">
                    <div className="p-2 rounded bg-slate-50 text-slate-700 flex items-center gap-1.5">
                      <BookOpen className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <div className="truncate">
                        <span className="text-slate-400 block text-[10px]">Subject Match</span>
                        <strong
                          className={
                            candidate.breakdown.subjectScore === 100
                              ? "text-emerald-700 font-semibold"
                              : "text-slate-700 font-semibold"
                          }
                        >
                          {candidate.breakdown.subjectScore}%
                        </strong>
                      </div>
                    </div>

                    <div className="p-2 rounded bg-slate-50 text-slate-700 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <div className="truncate">
                        <span className="text-slate-400 block text-[10px]">Timezone Offset</span>
                        <strong
                          className={
                            candidate.breakdown.timezoneOffsetHours === 0
                              ? "text-emerald-700 font-semibold"
                              : "text-slate-800 font-semibold"
                          }
                        >
                          {candidate.breakdown.timezoneOffsetHours}h offset
                        </strong>
                      </div>
                    </div>

                    <div className="p-2 rounded bg-slate-50 text-slate-700 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <div className="truncate">
                        <span className="text-slate-400 block text-[10px]">Active Batch Load</span>
                        <strong className="text-slate-800 font-semibold">
                          {candidate.breakdown.activeBatchCount} /{" "}
                          {candidate.breakdown.maxBatchCapacity}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
          <span>Selecting a tutor fills the assignment and updates batch milestones.</span>
          <Button variant="outline" size="sm" onClick={onClose} className="text-xs h-8">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default TutorMatchmakingModal;

